import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatUtcToLocalDateTimeString } from "@application/common/helpers/date-util";
import { NodeLogFilters } from "./NodeLogFilters";
import { NodeLogChartCard } from "./NodeLogChartCard";
import { NodeLogTableCard } from "./NodeLogTableCard";
import { EventFlagChip } from "./EventLog";
import { SensorType, isSensorHidden } from "@ui/utils/sensors";
/** The sensor (API `SensorType`) each series belongs to, to leave out the ones switched off on the Edit Node page. */
const SENSOR_OF_SERIES = {
    internalTemperature: SensorType.InternalTemperature,
    externalTemperature: SensorType.ExternalTemperature,
    humidity: SensorType.Humidity,
    co2: SensorType.Co2,
};
const SERIES_BY_READINGS = {
    apNode: ["internalTemperature", "co2", "humidity"],
    environmentNode: ["internalTemperature", "externalTemperature", "humidity"],
    temperatureOnly: ["internalTemperature"],
};
/** Shown in the event-log humidity column: event logs carry no humidity. */
const NO_VALUE = "—";
/**
 * Node-log page body for new-hardware access points (Figma: Nodes -> Log).
 * The temperature log shows CO2 / external temperature / humidity from the log response;
 * the event log has no such data, so its humidity column shows a dash.
 */
export const NodeLogNewLayout = ({ nodeName, readings, disabledSensors, headingLoading, replacementNotice, filters, chartLogs, chartLoading, temperatureLogData, temperatureLogLoading, temperatureLogCount, temperatureLogPagination, onTemperatureLogPagination, eventLogData, eventLogLoading, eventLogCount, eventLogPagination, onEventLogPagination, }) => {
    const { t } = useTranslation("common");
    const humidityColumn = (width) => ({
        field: "humidity",
        header: t("labels.humidity"),
        width,
        render: () => NO_VALUE,
    });
    const dateTime = (row) => formatUtcToLocalDateTimeString(row.dateTime);
    const seriesKeys = SERIES_BY_READINGS[readings].filter((key) => !isSensorHidden(disabledSensors, SENSOR_OF_SERIES[key]));
    const showTemperature = seriesKeys.includes("internalTemperature");
    const showHumidity = seriesKeys.includes("humidity");
    const temperatureColumns = [
        { field: "dateTime", header: t("grid.dateTime"), width: 170, render: dateTime },
        ...(showTemperature
            ? [{ field: "temperature", header: t("grid.temperature"), width: 105 }]
            : []),
        ...(seriesKeys.includes("co2")
            ? [{ field: "co2", header: t("labels.co2"), width: 105 }]
            : []),
        ...(seriesKeys.includes("externalTemperature")
            ? [
                {
                    field: "externalTemperature",
                    header: t("labels.externalTemperature"),
                    width: 130,
                },
            ]
            : []),
        ...(showHumidity
            ? [{ field: "humidity", header: t("labels.humidity"), width: 105 }]
            : []),
    ];
    const eventColumns = [
        { field: "dateTime", header: t("grid.dateTime"), width: 170, render: dateTime },
        {
            field: "flag",
            header: t("grid.flag"),
            width: 130,
            render: (row) => React.createElement(EventFlagChip, { flag: row.flag }),
        },
        {
            field: "battery",
            header: t("grid.battery"),
            width: 90,
            render: (row) => row.battery || "N/A",
        },
        {
            field: "version",
            header: t("grid.version"),
            width: 70,
            render: (row) => { var _a; return (_a = row.version) !== null && _a !== void 0 ? _a : "N/A"; },
        },
        ...(showTemperature
            ? [{ field: "temperature", header: t("grid.temperature"), width: 118 }]
            : []),
        ...(showHumidity ? [humidityColumn(110)] : []),
    ];
    return (React.createElement(Box, null,
        headingLoading ? (React.createElement(Skeleton, { variant: "text", width: 200, height: 36 })) : (React.createElement(Typography, { variant: "h4", color: "primary.darker" }, t("headings.nodeLogging", { name: nodeName }))),
        replacementNotice,
        React.createElement(Box, { sx: { mt: "36px" } },
            React.createElement(NodeLogFilters, Object.assign({}, filters))),
        React.createElement(Box, { sx: { mt: 2, display: "flex", flexDirection: "column", gap: "8px" } },
            seriesKeys.length === 0 ? null : chartLoading ? (React.createElement(Skeleton, { variant: "rounded", height: 496, sx: { borderRadius: "32px" } })) : (React.createElement(NodeLogChartCard, { logs: chartLogs, title: nodeName, seriesKeys: seriesKeys })),
            React.createElement(Box, { sx: {
                    display: "grid",
                    gap: "8px",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 420fr) minmax(0, 728fr)" },
                } },
                React.createElement(NodeLogTableCard, { title: t("headings.temperatureLog"), columns: temperatureColumns, rows: temperatureLogData, loading: temperatureLogLoading, emptyText: t("status.noData"), rowCount: temperatureLogCount, paginationModel: temperatureLogPagination, onPaginationModelChange: onTemperatureLogPagination }),
                React.createElement(NodeLogTableCard, { title: t("headings.eventLog"), columns: eventColumns, rows: eventLogData, loading: eventLogLoading, emptyText: t("status.noData"), rowCount: eventLogCount, paginationModel: eventLogPagination, onPaginationModelChange: onEventLogPagination })))));
};
